import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { CalendarClock, ExternalLink, AlertTriangle, RefreshCw } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface PtAdvisory {
  id: string
  title: string
  severity: string
  cvss: number | null
  cves: string[]
  url: string
  published_at: string | null
}
interface PtMonth {
  month: string  // YYYY-MM
  advisories: PtAdvisory[]
  cve_count: number
  critical_count: number
  triaged_count: number
  untriaged_count: number
}

const SEV_TONE: Record<string, string> = {
  critical: 'bg-rose/15 text-rose-light border-rose/40',
  high:     'bg-orange/15 text-orange border-orange/40',
  medium:   'bg-amber/15 text-amber-light border-amber/40',
  low:      'bg-blue/15 text-blue-light border-blue/40',
  unknown:  'bg-surface-raised text-text-muted border-border',
}

export default function PatchTuesdayPage() {
  const [months, setMonths] = useState<PtMonth[]>([])
  const [loading, setLoading] = useState(true)
  const [windowMonths, setWindowMonths] = useState(3)
  const [stats, setStats] = useState<{ total_cves: number; total_triaged: number } | null>(null)

  const fetchAll = (n = windowMonths) => {
    setLoading(true)
    api.get('/api/cyber/patch-tuesday-triage', { params: { months_back: n } })
      .then(r => {
        setMonths(r.data?.months || [])
        setStats({ total_cves: r.data?.total_cves || 0, total_triaged: r.data?.total_triaged || 0 })
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => { fetchAll(windowMonths) }, [windowMonths])

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <CalendarClock size={24} className="text-blue" /> Patch Tuesday Triage
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            Microsoft MSRC monthly cycles with per-CVE triage cross-reference.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={windowMonths} onChange={e => setWindowMonths(parseInt(e.target.value, 10))}
            className="input py-1.5 text-xs">
            <option value={1}>1 month</option>
            <option value={2}>2 months</option>
            <option value={3}>3 months</option>
            <option value={6}>6 months</option>
          </select>
          <button type="button" onClick={() => fetchAll(windowMonths)} className="btn-secondary text-xs">
            <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-3 gap-3">
          <div className="card p-3">
            <div className="text-[10px] uppercase tracking-wider text-text-muted">CVEs in window</div>
            <div className="text-2xl font-bold text-text-primary font-mono">{stats.total_cves}</div>
          </div>
          <div className="card p-3">
            <div className="text-[10px] uppercase tracking-wider text-text-muted">Triaged</div>
            <div className="text-2xl font-bold text-emerald-light font-mono">{stats.total_triaged}</div>
          </div>
          <div className="card p-3">
            <div className="text-[10px] uppercase tracking-wider text-text-muted">Untriaged</div>
            <div className="text-2xl font-bold text-amber-light font-mono">{stats.total_cves - stats.total_triaged}</div>
          </div>
        </div>
      )}

      {loading && <div className="card p-6 text-center text-text-muted text-sm">Loading…</div>}

      {!loading && months.length === 0 && (
        <div className="card p-6 text-center text-text-muted text-sm">
          No Microsoft advisories in the selected window. Check your CSAF / advisory ingest is active.
        </div>
      )}

      {months.map(m => (
        <div key={m.month} className="card p-4">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h2 className="text-base font-semibold text-text-primary">{m.month}</h2>
            <div className="flex items-center gap-3 text-[11px]">
              <span className="text-text-muted">{m.advisories.length} advisories</span>
              <span className="text-rose-light font-mono">{m.critical_count} critical</span>
              <span className="text-text-muted">·</span>
              <span className="text-emerald font-mono">{m.triaged_count} triaged</span>
              <span className="text-amber-light font-mono">{m.untriaged_count} pending</span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-text-muted border-b border-border">
                  <th className="text-left py-2 px-2">Advisory</th>
                  <th className="text-left py-2 px-2">Severity</th>
                  <th className="text-right py-2 px-2">CVSS</th>
                  <th className="text-left py-2 px-2">CVEs</th>
                  <th className="text-right py-2 px-2"></th>
                </tr>
              </thead>
              <tbody>
                {m.advisories.slice(0, 30).map(a => (
                  <tr key={a.id} className="border-b border-border/50 hover:bg-surface-hover">
                    <td className="py-1.5 px-2 text-text-primary truncate max-w-[400px]">{a.title}</td>
                    <td className="py-1.5 px-2">
                      <span className={clsx('px-1.5 py-0.5 rounded border text-[9px] uppercase font-mono',
                        SEV_TONE[a.severity] || SEV_TONE.unknown)}>
                        {a.severity}
                      </span>
                    </td>
                    <td className="py-1.5 px-2 text-right font-mono">{a.cvss?.toFixed(1) || '—'}</td>
                    <td className="py-1.5 px-2">
                      <div className="flex flex-wrap gap-1">
                        {a.cves.slice(0, 3).map(c => (
                          <Link key={c} to={`/app/cve/${encodeURIComponent(c)}`}
                            className="font-mono text-[9px] px-1 py-0.5 rounded bg-rose/10 text-rose-light hover:bg-rose/20">
                            {c}
                          </Link>
                        ))}
                        {a.cves.length > 3 && <span className="text-[9px] text-text-muted">+{a.cves.length - 3}</span>}
                      </div>
                    </td>
                    <td className="py-1.5 px-2 text-right">
                      <a href={a.url} target="_blank" rel="noreferrer noopener" className="text-blue-light hover:underline">
                        <ExternalLink size={10} />
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  )
}
